/**
 * The props of a menu item (context menu, dropdown, radio item) that is unavailable but must still explain why (decision
 * P3; the tree menu's pattern, `TreeNodeContextMenu`): Radix's own `disabled` adds `pointer-events-none`, which also
 * blocks the hover — and with it the `title` tooltip — so the item stays pointer-enabled, is styled muted, carries
 * `aria-disabled` + the reason, and swallows its select. No reason → no extra props.
 */
export type ReasonMenuItemProps = {
  'aria-disabled'?: 'true';
  title?: string;
  'data-disabled-reason'?: string;
  className?: string;
  onSelect?: (event: Event) => void;
};

/**
 * UI/UX batch C2 N7: the item used to be `opacity-50` as a whole, so the reason line under it — the one line meant to
 * explain the item — measured 2.1:1, the faintest text on screen. Now the text is `muted-foreground` at full opacity
 * (≥ 4.5:1 on the popover, light and dark) and only the icon is dimmed; the reason line keeps `muted-foreground` too.
 * Keyboard (and hover) focus shows a light wash, so the user can see which unavailable item the reason belongs to.
 */
export const REASON_MENU_ITEM_CLASS =
  'cursor-not-allowed text-muted-foreground hover:bg-transparent hover:text-muted-foreground focus:bg-accent/50 focus:text-muted-foreground [&_svg]:opacity-60';

export function reasonMenuItemProps(reason: string | undefined): ReasonMenuItemProps {
  if (!reason) return {};
  return { 'aria-disabled': 'true', title: reason, 'data-disabled-reason': reason, className: REASON_MENU_ITEM_CLASS, onSelect: (event) => event.preventDefault() };
}
